import { useState } from 'react';
import clsx from 'clsx';
import { formatDate } from '../../lib/format';
import {
  resumeReminders,
  snoozeReminders,
  type SnoozeSpoor,
} from '../../lib/snooze-reminders';

interface SnoozeRemindersDialogProps {
  orderId: string;
  orderNumber: string;
  /** Is het aflever-spoor relevant (in sequence of al gepauzeerd)? */
  afleverRelevant: boolean;
  /** Is het ophaal-spoor relevant? */
  ophaalRelevant: boolean;
  afleverPauzeTot: string | null;
  ophaalPauzeTot: string | null;
  onClose: () => void;
  onSaved: () => void;
}

function todayIso(): string {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60_000).toISOString().slice(0, 10);
}

const SPOOR_LABEL: Record<SnoozeSpoor, string> = {
  aflever: 'Aflever-reminders',
  ophaal: 'Ophaal-reminders',
  beide: 'Beide sporen',
};

export function SnoozeRemindersDialog({
  orderId,
  orderNumber,
  afleverRelevant,
  ophaalRelevant,
  afleverPauzeTot,
  ophaalPauzeTot,
  onClose,
  onSaved,
}: SnoozeRemindersDialogProps) {
  const both = afleverRelevant && ophaalRelevant;
  // Beschikbare sporen: alleen wat relevant is. Bij beide ook 'beide' aanbieden.
  const spoorOptions: SnoozeSpoor[] = both
    ? ['beide', 'aflever', 'ophaal']
    : afleverRelevant
      ? ['aflever']
      : ['ophaal'];

  const [spoor, setSpoor] = useState<SnoozeSpoor>(spoorOptions[0] ?? 'beide');
  const [tot, setTot] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const today = todayIso();
  const dateValid = !!tot && tot >= today;

  // Bestaande pauze(s) voor het gekozen spoor — voedt de "Hervat nu"-knop.
  const activePauses: { spoor: SnoozeSpoor; tot: string }[] = [];
  if ((spoor === 'aflever' || spoor === 'beide') && afleverPauzeTot) {
    activePauses.push({ spoor: 'aflever', tot: afleverPauzeTot });
  }
  if ((spoor === 'ophaal' || spoor === 'beide') && ophaalPauzeTot) {
    activePauses.push({ spoor: 'ophaal', tot: ophaalPauzeTot });
  }

  const handleSnooze = async () => {
    if (!dateValid) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await snoozeReminders({ werkOrderId: orderId, spoor, tot });
      if (!result.ok) {
        setError(result.error || result.message || 'Pauzeren mislukt');
        return;
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Pauzeren mislukt');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResume = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await resumeReminders({ werkOrderId: orderId, spoor });
      if (!result.ok) {
        setError(result.error || result.message || 'Hervatten mislukt');
        return;
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Hervatten mislukt');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Reminders pauzeren</h2>
          <p className="text-xs text-slate-500 mt-1">Order {orderNumber}</p>
        </div>

        <div className="px-6 py-4 space-y-3">
          <p className="text-sm text-slate-700">
            Gebruik dit als de klant op vakantie is of pas later op-/afgeleverd wil
            worden. Tijdens de pauze gaan er geen herinneringen uit. Daarna hervat de
            cadence vanzelf vanaf de gekozen datum
            {spoor !== 'ophaal' && ' en starten ook de opslagkosten pas vanaf dan'}.
          </p>

          {spoorOptions.length > 1 ? (
            <div>
              <span className="text-sm text-slate-700">Welk spoor?</span>
              <div className="mt-1.5 space-y-1.5">
                {spoorOptions.map((opt) => (
                  <label
                    key={opt}
                    className={clsx(
                      'flex items-center gap-2 rounded border px-3 py-2 cursor-pointer',
                      spoor === opt
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-300 hover:border-slate-400'
                    )}
                  >
                    <input
                      type="radio"
                      name="snooze-spoor"
                      value={opt}
                      checked={spoor === opt}
                      onChange={() => setSpoor(opt)}
                      disabled={submitting}
                    />
                    <span className="text-sm text-slate-900">{SPOOR_LABEL[opt]}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-700">
              Spoor: <span className="font-medium">{SPOOR_LABEL[spoor]}</span>
            </p>
          )}

          {activePauses.length > 0 && (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 space-y-1">
              {activePauses.map((p) => (
                <p key={p.spoor} className="text-xs text-amber-800">
                  ⏸ {SPOOR_LABEL[p.spoor]} gepauzeerd t/m {formatDate(p.tot)}
                </p>
              ))}
            </div>
          )}

          <label className="block">
            <span className="text-sm text-slate-700">Pauzeren t/m (terugkeerdatum)</span>
            <input
              type="date"
              value={tot}
              min={today}
              onChange={(e) => setTot(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              disabled={submitting}
            />
            {tot && !dateValid && (
              <p className="text-xs text-amber-600 mt-1">
                Datum mag niet in het verleden liggen.
              </p>
            )}
            <p className="text-xs text-slate-500 mt-1">
              Laat leeg en klik "Pauzeren" om standaard 21 dagen te pauzeren.
            </p>
          </label>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between gap-2">
          {activePauses.length > 0 ? (
            <button
              type="button"
              onClick={handleResume}
              disabled={submitting}
              className="text-sm font-medium text-slate-600 hover:text-slate-900 px-3 py-1.5 disabled:opacity-50"
            >
              {submitting ? 'Bezig…' : 'Hervat nu'}
            </button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
            >
              Sluiten
            </button>
            <button
              type="button"
              onClick={handleSnooze}
              disabled={submitting || (!!tot && !dateValid)}
              className={clsx(
                'text-sm font-medium px-3 py-1.5 rounded',
                submitting || (!!tot && !dateValid)
                  ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              )}
            >
              {submitting ? 'Pauzeren…' : 'Pauzeren'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
